import {
  CreateOrderResponse,
  InstrumentCategoryEnum,
  isDefined,
  ListedInstrument,
  Loadable,
  OrderExchangeData,
  OrderResponseType,
  OrderWarningsResponse,
  TempOrder,
  TradeType,
  useLatest,
} from '@eih-netbroker/common';
import { useCallback, useMemo, useState } from 'react';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { useSnackbar } from '../../../../context/SnackbarContext';
import { TextHeaderDialogProps } from '../../../../shared/components/dialog-presets/TextHeaderDialog';
import { useAccounts } from '../../../../shared/services/useAccounts';
import { useOpenOrders } from '../../../../shared/services/useOpenOrders';
import { useUserSettings } from '../../../../shared/services/user-settings/useUserSettings';
import { OrderCurrencyExchangeParams, OrderFormData } from '../../components/order/OrderForm';
import { useOrderState } from '../../components/OrderStateContext';
import { CapturedExchangeData, OrderFlowStep } from '../../types/orderTypes';
import { createEmptyOrderForm, summarizedExchangeData } from '../../utils/orderUtils';
import { useFuturesInfo } from './useFuturesInfo';
import { useCreateNormalOrder, ValidatedOrder } from './useNormalOrderView';

type UseOrderModalProps = {
  onCancel: (shouldResetFormToDefaults: boolean) => void;
  order?: OrderFormData;
  orderId?: string;
};

type ModalOptions = Pick<TextHeaderDialogProps, 'headerText' | 'withHeaderDivider' | 'withFooterDivider'>;

export const useOrderModalNew = ({ onCancel, order, orderId }: UseOrderModalProps) => {
  const {
    setTempOrder,
    tempOrder,
    continueOrder,
    setWarnings,
    setSmsCode,
    setSmsCodeError,
    warnings,
    setOrderFlowStep,
  } = useOrderState();
  const { loadFuturesInitialMargin } = useFuturesInfo();
  const { getOrderDefaults } = useUserSettings();
  const [orderState, setOrderState] = useState<OrderFormData>({
    ...getOrderDefaults(order?.instrument, order?.orderType),
    ...order,
  });
  const {
    createOrder,
    modifyOrder,
    confirmOrder,
    validateLimitPrice,
    validateStopActivationPrice,
    priceValidationState,
    stopActivationPriceValidationState,
    generateSmsCode,
    setIsLoading,
  } = useCreateNormalOrder();
  const { translation } = useLocale();
  const { loadOrders } = useOpenOrders();
  const { selectedAccountOrClient } = useAccounts();
  const { showSuccess } = useSnackbar();
  // Captured exchange data is stored in a state so that it can be used for rendering (SMS modal), but is passed around
  // in callbacks (and not used from state), so that useCallbacks don't get stale versions of this.
  const [capturedExchangeData, setCapturedExchangeData] = useState<CapturedExchangeData>({
    exchangeRate: undefined,
    summary: undefined,
  });

  const initialOrder = useMemo(() => {
    if (order && orderId) {
      // when modifying an order, we want to keep the original order values
      // it must be ValidatedOrder to have all required fields
      return order as ValidatedOrder;
    }

    return null;
  }, [order, orderId]);

  const lastPriceRef = useLatest(orderState.price);
  const lastStopPriceRef = useLatest(orderState.stopActivationPrice);

  const updateOrderState = useCallback(
    (order: OrderFormData) => {
      if (order.price !== lastPriceRef.current) {
        validateLimitPrice(order);
      }

      if (order.stopActivationPrice !== lastStopPriceRef.current) {
        validateStopActivationPrice(order);
      }

      setOrderState({ ...getOrderDefaults(order.instrument, order.orderType), ...order });
    },
    [getOrderDefaults, lastPriceRef, lastStopPriceRef, validateLimitPrice, validateStopActivationPrice],
  );

  const changeInstrument = useCallback(
    (instrument: ListedInstrument | null) => {
      if (instrument?.instrumentCategory === InstrumentCategoryEnum.Futures) {
        loadFuturesInitialMargin(instrument.ticker);
      }

      if (!isDefined(instrument)) {
        setOrderState(createEmptyOrderForm(getOrderDefaults(instrument, undefined)));
        return;
      }

      setOrderState({
        ...getOrderDefaults(instrument, undefined),
        instrument,
        price: undefined,
        stopActivationPrice: undefined,
      });
    },
    [loadFuturesInitialMargin, getOrderDefaults],
  );

  const updateExchangeParams = useCallback((currencyExchangeParams: OrderCurrencyExchangeParams | undefined) => {
    setOrderState(cur => ({
      ...cur,
      currencyExchangeParams,
    }));
  }, []);

  const resetStates = useCallback(() => {
    setOrderFlowStep(OrderFlowStep.Form);
    setTempOrder(undefined);
    setWarnings(undefined);
    setSmsCode(undefined);
    setSmsCodeError(undefined);
    setCapturedExchangeData({ exchangeRate: undefined, summary: undefined });
  }, [setOrderFlowStep, setSmsCode, setSmsCodeError, setTempOrder, setWarnings]);

  const handleModalOnClose = useCallback(() => {
    resetStates();
    onCancel(false);
  }, [resetStates, onCancel]);

  const handleOnBack = useCallback(() => {
    if (warnings || tempOrder) {
      resetStates();
    } else {
      setOrderFlowStep(OrderFlowStep.Form);
      onCancel(false);
    }
  }, [warnings, tempOrder, onCancel, resetStates, setOrderFlowStep]);

  const handleCreateOrModifyOrderResponse = useCallback(
    async (order: CreateOrderResponse, isModify: boolean) => {
      if (order.$type === OrderResponseType.TempOrder) {
        setOrderFlowStep(OrderFlowStep.Confirmation);
        setTempOrder(order as TempOrder);
      } else if (order.$type === OrderResponseType.Order) {
        setOrderFlowStep(OrderFlowStep.Form);
        showSuccess(isModify ? translation.orderModal.modifyOrderSuccess : translation.orderModal.createOrderSuccess);
        loadOrders(false);
        onCancel(true);
      } else if (order.$type === OrderResponseType.Warning) {
        setOrderFlowStep(OrderFlowStep.ReceivedMessages);
        setWarnings((order as OrderWarningsResponse).messages ?? []);
      }
    },
    [setTempOrder, showSuccess, translation.orderModal, setWarnings, loadOrders, setOrderFlowStep, onCancel],
  );

  const createOrModifyOrder = useCallback(
    async (validate: boolean, exchangeData?: OrderExchangeData) => {
      const currencyExchangeParams: OrderCurrencyExchangeParams | undefined =
        isDefined(exchangeData) && Loadable.hasValue(exchangeData.orderFields)
          ? {
              sourceCurrency: exchangeData.orderFields.currencySourceCurrency,
              targetAmount: exchangeData.orderFields.currencyTargetAmount,
              sourceAmount: exchangeData.orderFields.currencySourceAmount,
              exchangeRate: exchangeData.orderFields.exchangeRate,
            }
          : undefined;

      if (isDefined(exchangeData) && isDefined(currencyExchangeParams)) {
        const exchange: CapturedExchangeData = {
          summary: summarizedExchangeData(exchangeData),
          exchangeRate: exchangeData.isEnabled ? exchangeData.exchangeRate : undefined,
        };
        setCapturedExchangeData(exchange);
      }

      if (orderState && orderState.instrument) {
        if (orderId) {
          const order = await modifyOrder(
            orderId,
            orderState.instrument,
            {
              ...orderState,
              currencyExchangeParams,
            } as ValidatedOrder,
            selectedAccountOrClient.account?.accountNumber!,
            validate,
          );

          await handleCreateOrModifyOrderResponse(order, true);
        } else {
          const order = await createOrder(
            orderState.instrument,
            {
              ...orderState,
              currencyExchangeParams,
            } as ValidatedOrder,
            selectedAccountOrClient.account?.accountNumber!,
            validate,
          );

          if (order) {
            await handleCreateOrModifyOrderResponse(order, false);
          }
        }
      }
    },
    [
      orderState,
      orderId,
      modifyOrder,
      selectedAccountOrClient.account?.accountNumber,
      handleCreateOrModifyOrderResponse,
      createOrder,
    ],
  );

  const onContinue = useCallback(
    async (exchangeData?: OrderExchangeData, smsCode?: string) => {
      setIsLoading(true);
      await continueOrder(async (twoFactor: boolean) => {
        if (warnings) {
          await createOrModifyOrder(false, exchangeData);
        } else {
          await confirmOrder(
            tempOrder?.tempOrderId!,
            twoFactor ? smsCode : undefined,
            !!orderId,
            orderState.currencyExchangeParams !== 'initialized' && !!orderState.currencyExchangeParams?.exchangeRate,
          );
        }
        onCancel(true);
      }, generateSmsCode);
      setIsLoading(false);
    },
    [
      orderState.currencyExchangeParams,
      continueOrder,
      confirmOrder,
      tempOrder,
      warnings,
      createOrModifyOrder,
      orderId,
      generateSmsCode,
      setIsLoading,
      onCancel,
    ],
  );

  const onConfirmHandle = useCallback(
    async (exchangeData?: OrderExchangeData) => {
      try {
        setIsLoading(true);
        await createOrModifyOrder(true, exchangeData);
      } finally {
        setIsLoading(false);
      }
    },
    [createOrModifyOrder, setIsLoading],
  );

  const handle2FARetryFlow = useCallback(
    async (handleOnBack: () => void, capturedExchangeData?: CapturedExchangeData) => {
      // In case of currency exchange, we should navigate back to the form screen
      // (or close the modal if initiated from widget)
      if (
        isDefined(capturedExchangeData) &&
        isDefined(capturedExchangeData.exchangeRate) &&
        isDefined(capturedExchangeData.summary)
      ) {
        handleOnBack();
      } else {
        // otherwise recreate the order and navigate to the confirmation screen
        setTempOrder(undefined);
        setWarnings(undefined);
        await onConfirmHandle();
        setSmsCode(undefined);
        setSmsCodeError(undefined);
      }
    },
    [onConfirmHandle, setSmsCode, setSmsCodeError, setTempOrder, setWarnings],
  );

  const modalOptions = useMemo<ModalOptions>(() => {
    if (!tempOrder && !warnings) {
      return {
        headerText: translation.orderModal.order,
        withHeaderDivider: true,
        withFooterDivider: true,
      };
    }

    if (warnings) {
      return {
        headerText: translation.orderModal.order,
        withHeaderDivider: true,
        withFooterDivider: true,
      };
    }

    if (tempOrder) {
      if (tempOrder.showWarningConfirmation) {
        return {
          headerText: translation.orderModal.order,
          withHeaderDivider: true,
          withFooterDivider: true,
        };
      }

      if (tempOrder.showConfirmation) {
        return orderState.tradeType === TradeType.Sell
          ? {
              headerText: translation.orderModal.confirmSell,
              withHeaderDivider: false,
              withFooterDivider: false,
            }
          : {
              headerText: translation.orderModal.confirmBuy,
              withHeaderDivider: false,
              withFooterDivider: false,
            };
      }
    }

    // Fallback title, should not happen
    return {
      headerText: translation.orderModal.order,
      withHeaderDivider: true,
      withFooterDivider: true,
    };
  }, [translation.orderModal, tempOrder, warnings, orderState.tradeType]);

  return {
    onContinue,
    onConfirmHandle,
    handleOnBack,
    handleModalOnClose,
    handle2FARetryFlow,
    changeInstrument,
    updateExchangeParams,
    updateOrderState,
    orderState,
    modalOptions,
    initialOrder,
    capturedExchangeData,
    priceValidationState,
    stopActivationPriceValidationState,
    validateStopActivationPrice,
  };
};
